import { useMemo, useState } from 'react';
import { Outlet, NavLink, useLocation } from 'react-router-dom';
import {
  Users, Buildings, EnvelopeSimple, ClockClockwise, EnvelopeOpen, CaretDown,
  UserCircle, ShieldCheck, List, Shield, Signature, FlowArrow, Database, Tag,
  TreeStructure, CalendarBlank, UserSwitch, ArrowsMerge, BellRinging, Warning,
  UsersThree, GitBranch, BookmarkSimple, Gear, HardDrives, NewspaperClipping, Hash,
  Sparkle, PauseCircle, Wrench, ShareNetwork, Desktop, MapPin, Cpu, Brain, Code,
  ClockCounterClockwise, MagnifyingGlass, FolderSimple, X,
} from '@phosphor-icons/react';
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from '@/components/ui/sheet';
import {
  useAuth, canManageEmployees, canManageUsers, canManageEmail, canViewAuditLog,
  isAdmin, isManager, canManageLabels, canBackup, canAdvancedSettings, canManageSku, canAiImportHistory,
} from '@/auth/AuthContext';
import { useT } from '@/i18n/LanguageContext';
import { GROUPS, GROUP_ORDER, SETTINGS_REGISTRY, searchRegistry } from '@/config/settingsRegistry';

// ── Icon dictionary — logical name → phosphor component ──────────────────
const ICONS = {
  Users, Buildings, EnvelopeSimple, ClockClockwise, EnvelopeOpen, UserCircle,
  ShieldCheck, Shield, Signature, FlowArrow, Database, Tag, TreeStructure,
  CalendarBlank, UserSwitch, ArrowsMerge, BellRinging, Warning, UsersThree,
  GitBranch, BookmarkSimple, Gear, HardDrives, NewspaperClipping, Hash, Sparkle,
  PauseCircle, Wrench, ShareNetwork, Desktop, MapPin, Cpu, Brain, Code,
  ClockCounterClockwise, MagnifyingGlass, FolderSimple,
};
const iconFor = (key) => ICONS[key] || Gear;

// ── Capability tokens → predicates. Kept in the layout so the registry
// stays framework-free.
const ALWAYS = () => true;
const CAP_FN = {
  ALWAYS,
  isAdmin, isManager,
  canManageEmployees, canManageUsers, canManageEmail, canViewAuditLog,
  canManageLabels, canBackup, canAdvancedSettings, canManageSku, canAiImportHistory,
};

function canSee(entry, user, pausedModules) {
  if (entry.deprecated) return false;
  if (entry.pausedModule && pausedModules.includes(entry.pausedModule)) return false;
  const fn = CAP_FN[entry.capability] || ALWAYS;
  return fn(user);
}


function NavItem({ entry, onNavigate }) {
  const Icon = iconFor(entry.iconKey);
  const slug = entry.path.split('/').pop();
  return (
    <NavLink
      to={entry.path}
      onClick={onNavigate}
      data-testid={`settings-nav-${slug}`}
      className={({ isActive }) =>
        `relative flex items-center gap-2.5 rounded-md px-3 py-2 text-sm transition-colors ${
          isActive
            ? 'bg-teal-50 text-teal-900 font-semibold shadow-[inset_0_0_0_1px_rgb(153_246_228_/_0.9)] before:absolute before:left-0 before:top-1 before:bottom-1 before:w-[3px] before:rounded-r before:bg-teal-700'
            : 'text-zinc-700 hover:bg-zinc-200/70 hover:text-zinc-900'
        }`
      }
    >
      {/* The row itself stays the full-width background carrier (px-3, hover/selected
          fill, left teal accent). The extra 8px hierarchy cue lives on this inner
          wrapper, so the icon and label move together and the backgrounds do not
          shrink or shift. pl-2 is the standard 8px spacing token. */}
      <span className="flex min-w-0 items-center gap-2.5 pl-2">
        <Icon size={17} weight="duotone" className="shrink-0" />
        <span className="truncate">{entry.labelJa}</span>
      </span>
    </NavLink>
  );
}

function GroupSection({ group, entries, expanded, active, onToggle, onNavigate }) {
  const Icon = iconFor(group.iconKey);
  return (
    <div className="mt-1" data-testid={`settings-group-${group.id}`}>
      <button
        type="button"
        onClick={() => onToggle(group.id)}
        aria-expanded={expanded}
        data-testid={`settings-group-${group.id}-toggle`}
        className={`w-full flex items-center justify-between gap-1 px-3 py-2 rounded-md text-sm font-semibold transition-colors ${
          active ? 'text-teal-700' : 'text-zinc-400 hover:text-zinc-600'
        }`}
      >
        {/* Typography and leading-icon size are deliberately identical to NavItem
            (text-sm / size={17} / gap-2.5 / duotone) — a category must never read
            as less prominent than its own children. Only colour distinguishes the
            expanded/active state, which is the existing design signal. */}
        <span className="flex min-w-0 items-center gap-2.5">
          <Icon size={17} weight="duotone" className="shrink-0" />
          <span className="truncate">{group.labelJa}</span>
        </span>
        <CaretDown size={11} weight="bold"
          className={`shrink-0 transition-transform duration-200 ${expanded ? 'rotate-180' : ''}`} />
      </button>
      {expanded && (
        <div className="mt-1 space-y-0.5 pl-1" data-testid={`settings-group-${group.id}-items`}>
          {entries.map((e) => (
            <NavItem key={`${e.path}#${e.labelJa}`} entry={e} onNavigate={onNavigate} />
          ))}
        </div>
      )}
    </div>
  );
}

// ── Settings-wide search (Phase 4) ────────────────────────────────────────
function SettingsSearch({ user, pausedModules, onNavigate }) {
  const [q, setQ] = useState('');
  const results = useMemo(() => {
    if (!q.trim()) return [];
    return searchRegistry(q)
      .filter((r) => canSee(r, user, pausedModules))
      .slice(0, 12);
  }, [q, user, pausedModules]);

  return (
    <div className="px-3 pt-3 pb-2 border-b border-zinc-100" data-testid="settings-search">
      <div className="relative">
        <MagnifyingGlass size={14} weight="bold"
          className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-400" />
        <input
          type="text"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="設定を検索（例: 図面, NAS, 索引, 学習）"
          data-testid="settings-search-input"
          className="w-full h-9 pl-8 pr-8 rounded-md border border-zinc-200 bg-white text-[12.5px] placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-teal-200 focus:border-teal-400"
        />
        {q && (
          <button type="button" onClick={() => setQ('')}
            data-testid="settings-search-clear"
            className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-700"
            aria-label="クリア">
            <X size={12} weight="bold" />
          </button>
        )}
      </div>
      {q && (
        <div className="mt-2 rounded-md border border-zinc-200 bg-white max-h-72 overflow-y-auto"
          data-testid="settings-search-results">
          {results.length === 0 ? (
            <p className="px-3 py-3 text-[11.5px] text-zinc-500" data-testid="settings-search-empty">
              「{q}」に一致する設定が見つかりません
            </p>
          ) : (
            results.map((r) => {
              const Icon = iconFor(r.iconKey);
              const slug = r.path.split('/').pop();
              return (
                <NavLink
                  key={`${r.path}#${r.labelJa}`}
                  to={r.path}
                  onClick={onNavigate}
                  data-testid={`settings-search-result-${slug}`}
                  className="flex items-start gap-2 px-3 py-2 border-b border-zinc-100 last:border-b-0 hover:bg-teal-50/40"
                >
                  <Icon size={14} weight="duotone" className="shrink-0 mt-[3px] text-zinc-500" />
                  <div className="min-w-0 flex-1">
                    <p className="text-[12.5px] font-semibold text-zinc-900 truncate">{r.labelJa}</p>
                    <p className="text-[11px] text-zinc-500 truncate">{r.purpose}</p>
                    <p className="text-[10.5px] text-zinc-400 mt-0.5">
                      <span className="font-medium">{GROUPS[r.group]?.labelJa || r.group}</span>
                      {r.capability && r.capability !== 'ALWAYS' && (
                        <span className="ml-1.5 inline-block px-1 py-0.5 rounded bg-zinc-100 text-zinc-600 font-mono text-[9.5px]">
                          {r.capability === 'isAdmin' ? '管理者のみ' : r.capability}
                        </span>
                      )}
                    </p>
                  </div>
                </NavLink>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}

function SidebarContent({ visibleGroups, expandedIds, onToggle, activeGroupId,
                         user, pausedModules, onNavigate }) {
  const { t } = useT();
  return (
    <>
      <div className="px-5 py-4 border-b border-zinc-100">
        <p className="text-[10px] font-semibold uppercase tracking-[0.25em] text-zinc-400">Sensbey</p>
        <p className="text-base font-semibold tracking-tight text-zinc-900 mt-1">{t('common.settings')}</p>
      </div>
      <SettingsSearch user={user} pausedModules={pausedModules} onNavigate={onNavigate} />
      <nav className="p-2 space-y-1">
        {visibleGroups.map((g) => (
          <GroupSection key={g.group.id} group={g.group} entries={g.entries}
            active={g.group.id === activeGroupId}
            expanded={expandedIds.has(g.group.id)}
            onToggle={onToggle} onNavigate={onNavigate} />
        ))}
      </nav>
    </>
  );
}

export default function SettingsLayout() {
  const location = useLocation();
  const { user } = useAuth();
  const { t } = useT();

  const pausedModules = useMemo(() => (user && user.paused_modules) || [], [user]);

  // Build the 9 visible groups from the registry, filtered by capability.
  const visibleGroups = useMemo(() => {
    return GROUP_ORDER
      .map((gid) => ({
        group: GROUPS[gid],
        entries: SETTINGS_REGISTRY.filter((r) => r.group === gid && canSee(r, user, pausedModules)),
      }))
      .filter((g) => g.entries.length > 0);
  }, [user, pausedModules]);

  // Active group = the group that owns the current path.
  const activeGroupId = useMemo(() => {
    for (const g of visibleGroups) {
      if (g.entries.some((e) => location.pathname.startsWith(e.path))) return g.group.id;
    }
    return null;
  }, [visibleGroups, location.pathname]);

  const [expandedIds, setExpandedIds] = useState(() => {
    const s = new Set();
    if (activeGroupId) s.add(activeGroupId);
    return s;
  });
  const toggleGroup = (id) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const effectiveExpanded = new Set(expandedIds);
  if (activeGroupId) effectiveExpanded.add(activeGroupId);

  const [mobileOpen, setMobileOpen] = useState(false);

  const activeLabel = useMemo(() => {
    for (const g of visibleGroups) {
      const m = g.entries.find((e) => location.pathname.startsWith(e.path));
      if (m) return m.labelJa;
    }
    return t('common.settings');
  }, [visibleGroups, location.pathname, t]);

  return (
    <div className="flex min-h-[calc(100vh-64px)] bg-zinc-50/40">
      <aside className="hidden lg:block w-64 shrink-0 bg-white border-r border-zinc-200 sticky top-16 self-start max-h-[calc(100vh-64px)] overflow-y-auto">
        <SidebarContent
          visibleGroups={visibleGroups}
          expandedIds={effectiveExpanded}
          onToggle={toggleGroup}
          activeGroupId={activeGroupId}
          user={user}
          pausedModules={pausedModules}
        />
      </aside>

      <main className="flex-1 min-w-0 p-4 sm:p-6 lg:p-8">
        <div className="lg:hidden mb-4 flex items-center justify-between gap-2 bg-white border border-zinc-200 rounded-lg px-3 py-2.5 shadow-sm">
          <div className="min-w-0">
            <p className="text-[10px] font-semibold uppercase tracking-[0.25em] text-zinc-400">{t('common.settings')}</p>
            <p className="text-sm font-bold text-zinc-900 truncate">{activeLabel}</p>
          </div>
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <button
                type="button"
                data-testid="settings-mobile-toggle"
                aria-label={t('settings.menuOpen')}
                className="inline-flex min-h-[36px] items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold text-zinc-700 bg-zinc-100 hover:bg-zinc-200 transition-colors"
              >
                <List size={14} weight="bold" />
                {t('settings.menu')}
              </button>
            </SheetTrigger>
            <SheetContent side="left" className="w-[280px] p-0" data-testid="settings-mobile-sheet">
              <SheetTitle className="sr-only">{t('settings.menuTitle')}</SheetTitle>
              <SidebarContent
                visibleGroups={visibleGroups}
                expandedIds={effectiveExpanded}
                onToggle={toggleGroup}
                activeGroupId={activeGroupId}
                user={user}
                pausedModules={pausedModules}
                onNavigate={() => setMobileOpen(false)}
              />
            </SheetContent>
          </Sheet>
        </div>

        <Outlet />
      </main>
    </div>
  );
}
